import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import Switch from '@components/v2/form/Switch/Switch';
import { Box, FormControlLabel, Stack } from '@mui/material';
import * as R from 'ramda';
import { useEffect } from 'react';
import { Controller, FormProvider, useWatch } from 'react-hook-form';
import { useBlockRefreshIf } from '../../../../../../hooks/useBlockRefreshIf';
import LangEnum from '../../../../../../lang/LangEnum';
import { useApplicationContext } from '../../../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { useOrderContext } from '../../../../contexts/Order.context';
import { CustomerOptionsCopyOrderRenderer } from '../../../../copy-order/components/CustomerOptionsCopyOrderRenderer';
import { CustomerToCopy } from '../../../../copy-order/types/copyOrderModal.types';
import { CopyOrderProductsToNewOrderFormProps } from './CopyOrderProductsToNewOrderForm.interfaces';

const getCustomerOptionLabel = (customer: CustomerToCopy) =>
  `${customer.commercialName} (${R.isNil(customer.code) ? '-' : customer.code})`;

const CopyOrderProductsToNewOrderForm = ({
  source,
  form,
  handleSubmit,
}: CopyOrderProductsToNewOrderFormProps) => {
  useBlockRefreshIf({ condition: form.formState.isSubmitting });
  const { isStore } = useApplicationContext();
  const { translate } = useTranslation();
  const { order } = useOrderContext();

  const address = order?.customer?.addresses?.find((address) => address.principal);
  const currentCustomer: CustomerToCopy | undefined = order?.customer
    ? {
        ...order.customer,
        code: order.customer.code ?? '',
        cpfcnpj: order.customer.cpfcnpj ?? '',
        companyName: order.customer.companyName ?? '',
        city: address?.address.city ?? '',
        state: address?.address.state ?? '',
      }
    : undefined;

  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = form;

  const includeCurrentStore = useWatch({
    control,
    name: 'includeCurrentStore',
  });

  const handleIncludeCurrentStoreChange = (checked: boolean) => {
    if (checked && currentCustomer) {
      setValue('customers', [currentCustomer]);
    }
    setValue('includeCurrentStore', checked);
  };

  useEffect(() => {
    if (isStore) {
      handleIncludeCurrentStoreChange(true);
    }
  }, []);

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Stack sx={{ gap: 4 }}>
          <Stack sx={{ gap: 1 }}>
            <Controller
              control={control}
              name="customers"
              render={({ field }) => (
                <AsyncAutocomplete<CustomerToCopy, false>
                  name={field.name}
                  label={translate(LangEnum.ORDER_COPY_DIALOG_INPUT_LABEL)}
                  value={field.value[0]?.id ?? null}
                  initialData={field.value}
                  disabled={includeCurrentStore || isSubmitting}
                  source={source}
                  getOptionLabel={getCustomerOptionLabel}
                  renderOption={(customer) => (
                    <CustomerOptionsCopyOrderRenderer customer={customer} />
                  )}
                  onChange={(_, customers) => field.onChange(customers)}
                />
              )}
            />
            <Controller
              control={control}
              name="includeCurrentStore"
              render={({ field }) => (
                <Switch
                  name={field.name}
                  label={translate(LangEnum.COPY_ORDER_PRODUCTS_USE_CURRENT_STORE)}
                  checked={field.value}
                  disabled={!currentCustomer || isSubmitting}
                  sx={{ ml: 0 }}
                  size="medium"
                  onChange={(_, checked) => handleIncludeCurrentStoreChange(checked)}
                />
              )}
            />
          </Stack>
          <Box>
            <Controller
              control={control}
              name="removeFromOrigin"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ ml: -0.75 }}
                  label={translate(LangEnum.COPY_ORDER_PRODUCTS_REMOVE_FROM_CURRENT_ORDER)}
                  control={
                    <Checkbox
                      name={field.name}
                      checked={field.value}
                      disabled={isSubmitting}
                      onChange={field.onChange}
                    />
                  }
                />
              )}
            />
          </Box>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default CopyOrderProductsToNewOrderForm;
